/**
 * The canvas's `keep` placement: frames an agent presents without focus are
 * placed beside the cursor (or the requested side) in the visible flow,
 * selected, persisted and brought into view by the camera. Canvas only; on
 * the wall such frames arrive as flashing cards instead.
 */
import {
  focusPresentedFramesInDesktopMode,
  shouldUseDesktopExpandedPresentation,
} from "@/lib/focus/focus-desktop-mode";
import { presentCursor } from "@/lib/canvas-cursor-presentation";
import {
  PRESENT_GAP,
  clamp,
  currentFlowCenter,
  flowPointForScreenPoint,
  presentSizeForSide,
  rectForVisibleFlowViewport,
  resolveAutoSide,
} from "@/lib/canvas-presentation-layout";
import type { PresentFramesRequest } from "@/lib/canvas-presentation-types";
import {
  persistFrameGeometryUpdates,
  type FrameGeometryUpdate,
} from "@/lib/frame-geometry-persistence";
import { unhideCanvasFrames } from "@/lib/reveal-canvas-frames";
import { useCanvasStore } from "@/stores/canvas-store";
import {
  SELECTED_FRAME_Z_BASE,
  demotedZIndex,
} from "@/stores/canvas-selection-z";
import { useDesktopMode } from "@/stores/desktop-mode-store";

type FrameBox = { x: number; y: number; width: number; height: number };

const STACK_GAP = 20;

export function placeKeptFrames(
  frameIds: string[],
  request: PresentFramesRequest,
  options: {
    targetDisplayId: number | null | undefined;
    allowMirrorDesktopFocus: boolean;
  },
): void {
  const { targetDisplayId } = options;
  const selected = new Set(frameIds);
  const screenshotContext = request.context === "screenshot";
  const placement = request.placement ?? "center";
  const shouldReposition =
    screenshotContext || targetDisplayId != null || placement !== "center";
  const updates: FrameGeometryUpdate[] = [];

  useCanvasStore.setState((current) => {
    const viewportRect = rectForVisibleFlowViewport();
    const center = currentFlowCenter();
    const cursorPoint = request.cursorPoint;
    const cursorPointMatchesTarget =
      cursorPoint &&
      (targetDisplayId == null ||
        cursorPoint.displayId == null ||
        cursorPoint.displayId === targetDisplayId);
    const cursorFlow = cursorPointMatchesTarget
      ? flowPointForScreenPoint(cursorPoint)
      : null;
    const anchor = cursorFlow ?? center;
    const side =
      placement === "cursor" || placement === "auto"
        ? resolveAutoSide(viewportRect, anchor)
        : placement;
    const size = presentSizeForSide(side, viewportRect, anchor);
    const total = frameIds.length;
    const positioned = new Map<string, FrameBox>();

    if (shouldReposition) {
      frameIds.forEach((id, index) => {
        let x = center.x - size.width / 2;
        let y = center.y - size.height / 2;
        if (side === "right" || side === "left") {
          x =
            side === "right"
              ? anchor.x + PRESENT_GAP
              : anchor.x - PRESENT_GAP - size.width;
          y =
            anchor.y -
            (total * size.height + (total - 1) * STACK_GAP) / 2 +
            index * (size.height + STACK_GAP);
        } else if (side === "bottom" || side === "top") {
          x =
            anchor.x -
            (total * size.width + (total - 1) * STACK_GAP) / 2 +
            index * (size.width + STACK_GAP);
          y =
            side === "bottom"
              ? anchor.y + PRESENT_GAP
              : anchor.y - PRESENT_GAP - size.height;
        }
        x = clamp(
          x,
          viewportRect.left + PRESENT_GAP,
          viewportRect.left + viewportRect.width - size.width - PRESENT_GAP,
        );
        y = clamp(
          y,
          viewportRect.top + PRESENT_GAP,
          viewportRect.top + viewportRect.height - size.height - PRESENT_GAP,
        );
        positioned.set(id, { x, y, width: size.width, height: size.height });
      });
    }

    return {
      nodes: current.nodes.map((node) => {
        const isSelected = selected.has(node.id);
        const pos = positioned.get(node.id);
        if (pos) {
          updates.push({
            id: node.id,
            position_x: Math.round(pos.x),
            position_y: Math.round(pos.y),
            width: pos.width,
            height: pos.height,
          });
        }
        return {
          ...node,
          selected: isSelected,
          zIndex: isSelected
            ? SELECTED_FRAME_Z_BASE + frameIds.indexOf(node.id)
            : demotedZIndex(node.zIndex),
          position: pos ? { x: pos.x, y: pos.y } : node.position,
          width: pos ? pos.width : node.width,
          height: pos ? pos.height : node.height,
          style: pos
            ? { ...node.style, width: pos.width, height: pos.height }
            : node.style,
          measured: pos
            ? { width: pos.width, height: pos.height }
            : node.measured,
        };
      }),
    };
  });

  unhideCanvasFrames(frameIds);

  presentCursor({ ...request, exclusionFrameIds: frameIds });
  persistFrameGeometryUpdates(updates);
  if (screenshotContext && !shouldUseDesktopExpandedPresentation()) {
    useDesktopMode.getState().openPeek("agent_frame_action");
  }
  focusPresentedFramesInDesktopMode(frameIds, {
    allowMirror: options.allowMirrorDesktopFocus,
  });

  setTimeout(() => {
    if (request.abortSignal?.aborted) return;
    const next = useCanvasStore.getState();
    if (frameIds.length === 1 && !shouldReposition) {
      next.panToFrame?.(frameIds[0] as string);
    } else {
      next.fitViewNodes?.(frameIds);
    }
  }, 50);
}
